import React from 'react';
import Helmet from 'react-helmet';
import Moment from 'moment';
import _ from 'lodash';

import InfinityScrollLoad from '../components/InfinityScrollLoad';
import { Modal } from '../components/Modal';
import { NotificationDetail } from '../components/modals/NotificationDetail';
import { MasterPageHeader } from '../components/PageHeader';
import { NotificationService } from '../services/Notification';
import LocalStorageService from '../services/utilities/LocalStorageService';
import {
  HEADER_CONSTANTS,
  DATE_TIME_FORMAT,
  LIST_NOTICES_TYPES,
} from '../utilities/constants';
import NotificationUtility from '../utilities/notificationUtility';
import NotificationMessages from '../utilities/notificationMessages';
import { NotificationConstants } from '../utilities/notificationConstants';
import { messageUtility } from '../utilities/messageUtility';
import { isNullOrEmpty } from '../utilities/stringUtil';
import { FieldService } from '../services/Fields';
import { isForemanRoleId } from '../utilities/roleUtility';
import SessionStorageService from '../services/utilities/SessionStorageService';

Moment.locale('ja');

export const STATUS = {
  CONFIRM: { VALUE: 1, NAME: '要確認' },
  ALL: { VALUE: 0, NAME: 'すべて' },
};

const defaultValue = {
  field_name: '',
  status: STATUS.ALL.VALUE,
  message_type_group_codes: _.map(LIST_NOTICES_TYPES, 'id'),
};

/**
 * @description 通知一覧画面
 */
export class Notifications extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userId: LocalStorageService.getUserId(), // ユーザーID
      dataList: [], // 取得通知情報
      loaded: false, // ページング用ロードフラグ
      modalState: false, // ダイアログ開閉用フラグ
      messages: NotificationMessages, // 通知メッセージ
      selectIndex: null,
      params: {
        offset: 0,
        limit: 30,
      }, // API用パラメータ
      isLoading: true, // ロード中フラグ
      selectedNotice: {}, //選択されたお知らせ
      transmissionErrorResultDialogOpened: false, //送信履歴ダイアログ開閉用フラグ
      visibleFieldsExpiredUsageModal: false,
      visibleUserAccessExpiredModal: false,
      visibleNoUserAccessInfo: false,
      total: 0,
      isSearchFormOpen: false,
      searchParams: _.cloneDeep(defaultValue),
      tempParams: _.cloneDeep(defaultValue),
      confirmingItem: {
        isStrong: false,
        data: null,
        index: null,
      },
    };
    this.searchButtonRef = React.createRef();
    this.isRequestingFlag = false;
  }

  componentDidMount() {
    this.setNotifications();
  }

  handleSearchParamsChange = (key, value) => {
    this.setState(({ tempParams }) => ({
      tempParams: {
        ...tempParams,
        [key]: value,
      },
    }));
  };

  searchExecution = () => {
    if (this.searchButtonRef.current) {
      this.searchButtonRef.current.disabled = true;
    }

    this.setState(
      {
        searchParams: _.cloneDeep(this.state.tempParams),
        dataList: [],
        loaded: false,
      },
      () => {
        this.setNotifications(() => {
          if (this.searchButtonRef.current) {
            this.searchButtonRef.current.disabled = false;
          }
          this.isRequestingFlag = false;
        });
      }
    );
  };

  handleClearParams = () => {
    if (this.isRequestingFlag) {
      return;
    }
    this.isRequestingFlag = true;
    this.setState(
      {
        tempParams: _.cloneDeep(defaultValue),
      },
      this.searchExecution
    );
  };

  isExpiredUsage = data => {
    const { field_usage_end_date } = data;

    if (!field_usage_end_date) {
      return false;
    }

    const usageEndDate = Moment(
      field_usage_end_date,
      DATE_TIME_FORMAT.slashDateFormat
    );
    const today = Moment();
    return usageEndDate.isBefore(today.subtract(1, 'day'));
  };

  toggleFieldsExpiredUsageModal = confirmingItem => {
    const { visibleFieldsExpiredUsageModal } = this.state;
    this.setState({
      visibleFieldsExpiredUsageModal: !visibleFieldsExpiredUsageModal,
      confirmingItem,
    });
  };

  closeUserAccessExpiredModal = () => {
    this.setState({
      visibleUserAccessExpiredModal: false,
    });
  };

  closeNoUserAccessInfoDialog = () => {
    this.setState({
      visibleNoUserAccessInfo: false,
    });
  };

  toggleSearchForm = () => {
    const { isSearchFormOpen } = this.state;
    this.setState({ isSearchFormOpen: !isSearchFormOpen });
  };

  getUserAccessStatus = async confirmingItem => {
    // field_idがない現場の遷移先である場合はそのままスキップする
    if (!confirmingItem.field_id) {
      return 'SKIP';
    }

    return await FieldService.checkAccessible(confirmingItem.field_id)
      .then(response => {
        if (response) {
          this.setState({
            visibleUserAccessExpiredModal: !response.data.accessible,
          });
        } else {
          this.setState({
            visibleNoUserAccessInfo: true,
          });
        }
        return response;
      })
      .catch(error => {
        // 元請BOXの管理者から現場監督が削除される場合、404なるためCatchする
        if (error.status === 404) {
          this.setState({
            visibleNoUserAccessInfo: true,
          });
        }
      });
  };

  /**
   * @description 画面遷移処理準備_通知強以外
   * @param {object} data 通知情報
   * @param {boolean} isStrong
   * @param {number} index
   */
  handleConfirm = (data, isStrong, index) => {
    if (!isStrong) {
      this.setState({
        modalState: true,
        selectIndex: index,
        visibleFieldsExpiredUsageModal: false,
        visibleUserAccessExpiredModal: false,
      });
      return;
    }

    if (isStrong) {
      const { message_type_id: messageTypeId, param1 } = data;
      if (messageTypeId === 304 && !isNullOrEmpty(param1)) {
        this.handleTransmissionErrorNoticeClicked(data);
      } else {
        this.setState({
          modalState: true,
          selectIndex: index,
          visibleFieldsExpiredUsageModal: false,
          visibleUserAccessExpiredModal: false,
        });
      }
    }
  };

  handleClickNotification = async (data, index, isStrong) => {
    if (!this.isNotificationHasConfirmLink(data)) {
      this.setState({
        modalState: true,
        selectIndex: index,
      });
      return;
    }
    const response = await this.getUserAccessStatus(data);
    if (response) {
      if (response === 'SKIP' || response.data.accessible) {
        if (this.isExpiredUsage(data)) {
          this.toggleFieldsExpiredUsageModal({
            isStrong,
            data,
            index,
          });
        } else {
          this.handleConfirm(data, isStrong, index);
        }
      }
    }
  };

  /**
   * @description 画面遷移処理準備_通知強
   * @param {object} data 通知情報
   */
  handleExecute = data => {
    NotificationUtility.executeActionNotificationStrong(
      data.message_type_id,
      {
        id: this.state.userId,
        display_start_date: data.display_start_date,
        param1: data.param1,
        field_id: data.field_id,
        target_date: data.target_date,
        notificationId: data.id,
        param_json: data.param_json,
      },
      this.props.vw
    );

    // 通知一覧APIのトリガーとして、前回の操作時間を削除して再ロードする
    SessionStorageService.removeLastNotificationLoaded();
  };

  handleExecuteOtherThanStrong = data => {
    NotificationUtility.executeActionNotificationOtherStrong(
      data.message_type_id,
      {
        id: this.state.userId,
        display_start_date: data.display_start_date,
        param1: data.param1,
        field_id: data.field_id,
        target_date: data.target_date,
        notificationId: data.id,
        param_json: data.param_json,
      },
      this.props.vw
    );
  };

  handleConfirmFieldExpiredUsage = () => {
    const { confirmingItem } = this.state;
    const { data, isStrong, index } = confirmingItem || {};
    this.setState({ visibleFieldsExpiredUsageModal: false });
    this.handleConfirm(data, isStrong, index);
  };

  /**
   * @description 取得通知情報レンダー
   */
  renderNotificationRow = (data, index) => {
    const isExpiredUsage = this.isExpiredUsage(data);
    /*
     * 通知強：未読であれば「要確認」ラベールあり/既読であれば「要確認」ラベールなし
     */
    const isStrong = NotificationUtility.isStrongNotification(data);
    const isMark = !data.read_flag && isStrong && !isExpiredUsage;
    const icon = NotificationUtility.hasIcon(data);
    const displayStartDate =
      data.display_start_date &&
      Moment(data.display_start_date).format(DATE_TIME_FORMAT.slashDateFormat);
    const { name, type } =
      _.find(LIST_NOTICES_TYPES, { id: data.message_type_group_code }) || {};

    return (
      <tr
        key={`table__item-${index}`}
        className={`table__item editable${
          isExpiredUsage ? ' table__item--deactive' : ''
        }`}
        onClick={() => this.handleClickNotification(data, index, isStrong)}
      >
        <td className='value-notification-table__description'>
          <div className='notification-description-item'>
            <div className='notification-description-item__header'>
              {isExpiredUsage && (
                <div
                  className='notification-description-item__label
                  notification-description-item__label--expired'
                >
                  利用終了
                </div>
              )}
              <div className='notification-description-item__title'>
                {data.field_name || ''}
              </div>
              {name && (
                <div
                  className={`notification-description-item__label
                notification-description-item__label--${type}`}
                >
                  {name}
                </div>
              )}
              {displayStartDate && (
                <div className='notification-description-item__time'>
                  {displayStartDate}
                </div>
              )}
            </div>
            <div className='notification-description-item__header'>
              {isMark && <span className='notification-unread'>要確認</span>}
              {icon && (
                <i
                  className={`notification-description-item__icon ${icon}--dark`}
                />
              )}
              <div className='notification-description-item__title'>
                {data.title}
              </div>
            </div>
            <div className='notification-description-item__body'>
              <div className='notification-description-item__info'>
                {data.content}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  isNotificationHasConfirmLink = data => {
    const { message_type_id: messageTypeId } = data;
    if (
      isForemanRoleId() &&
      messageTypeId === NotificationConstants.fieldTreeLinked.messageTypeId // 46 #施工体系図紐付き
    ) {
      return false;
    }
    if (
      [47, 51, 52, 53].includes(messageTypeId) ||
      !NotificationUtility.hasLink(data)
    ) {
      return false;
    }
    return true;
  };

  handleTransmissionErrorNoticeClicked = data => {
    NotificationUtility.read(data);
    this.setState({
      selectedNotice: data,
      transmissionErrorResultDialogOpened: true,
    });
  };

  /**
   * @description 通知強ダイアログ
   */
  renderNotificationDetailModal = () => {
    if (this.state.selectIndex === null) {
      return null;
    }
    const data = this.state.dataList[this.state.selectIndex];
    const workNotCopyMessageTypeId =
      NotificationConstants.conferenceWorkPlansNotEnteredAndNotCopyNotification
        .messageTypeId;
    let isWorkCopyFinished = false;
    if (data.message_type_id === workNotCopyMessageTypeId && data.read_flag) {
      isWorkCopyFinished = true;
    }
    const isStrong = NotificationUtility.isStrongNotification(data);
    const hasConfirmLink = this.isNotificationHasConfirmLink(data);

    return (
      <NotificationDetail
        dialogTitle={data.title}
        dialogContent={
          isWorkCopyFinished
            ? messageUtility.getMessage('I-001-322')
            : data.content
        }
        dialogBlueButtonTitle={
          isStrong ? NotificationUtility.hasBlueButtonTitle(data) : '遷移する'
        }
        isHideBlueButton={isWorkCopyFinished || !hasConfirmLink}
        onClose={() => this.setState({ modalState: false, selectIndex: null })}
        onExecute={() => {
          if (isStrong) {
            NotificationUtility.read(data);
            this.handleExecute(data);
          } else {
            this.handleExecuteOtherThanStrong(data);
          }
          this.setState({ modalState: false, selectIndex: null });
        }}
      />
    );
  };

  /**
   * @description 通知情報取得
   * @param {*} notify
   */
  setNotifications(notify) {
    if (this.state.loaded && notify) {
      notify();
      this.setState({ isLoading: false });
      return;
    }
    this.setState({ isLoading: true });
    const { dataList, params, searchParams } = this.state;

    NotificationService.list({
      ...searchParams,
      offset: notify ? _.size(dataList) : 0,
      limit: params.limit,
      order: 'display_start_date_desc',
      field_id: SessionStorageService.getFieldId(),
    }).then(response => {
      if (response.result) {
        const { notices: retrievedNotifications } = response.data.entities;
        if (_.isEmpty(retrievedNotifications) && notify) {
          this.setState({
            loaded: true,
            isLoading: false,
            total: _.get(response, 'data.total', 0),
          });
        } else {
          const currentNotifications = notify ? _.cloneDeep(dataList) : [];
          // 現在日時 < display_start_date の場合、通知一覧に表示しない
          // 現在日時 > display_end_date の場合、通知一覧に表示しない
          // display_end_dateに値が設定されていない場合は無期限表示
          _.each(retrievedNotifications, notification => {
            const { display_end_date, display_start_date } = notification;
            const noEndDate = _.isNull(display_end_date);
            const beforeStartDate = Moment().isBefore(display_start_date);
            const notInDisplayPeriod = !Moment().isBetween(
              display_start_date,
              display_end_date
            );

            if (
              (noEndDate && beforeStartDate) ||
              (!noEndDate && notInDisplayPeriod)
            ) {
              return;
            }
            currentNotifications.push(notification);
          });
          this.setState({
            dataList: currentNotifications,
            loaded: false,
            isLoading: false,
            total: _.get(response, 'data.total', 0),
          });

          // 通知一覧APIのトリガーとして、前回の操作時間を削除して再ロードする
          SessionStorageService.removeLastNotificationLoaded();
        }
      }
    }).finally(() => {
      if (notify) {
        notify();
      }
    });
  }

  render() {
    const { dataList, isLoading, modalState } = this.state;

    return (
      <div id='main' className='main'>
        <Helmet>
          <title>{HEADER_CONSTANTS.conference} - 通知一覧</title>
        </Helmet>
        <MasterPageHeader title={'通知一覧'} />
        {/* layout.jsにclassNameがmain__bodyを対象にした高さ調整の処理があるため
            main__bodyにしないと高さが合わず下までスクロールしても見切れる */}
        <div id='notification_list' className='main__body notifications__body'>
          <div className='notification-header'>
            <div className='notification-header__item'>
              <div className='notification-header__info'>
                <span className='btn--contained'>確認</span>
                <span>ページが切り替わります</span>
              </div>
              <div className='notification-header__info'>
                <span className='btn--text'>確認</span>
                <span>ポップアップが表示されます</span>
              </div>
            </div>
          </div>
          <table className='table table--notification-table'>
            <thead className='table__header' />
            <tbody>
              {_.map(dataList, (data, index) =>
                this.renderNotificationRow(data, index)
              )}
            </tbody>
            {!isLoading && _.isEmpty(dataList) && (
              <tbody>
                <tr>
                  <td>通知はありません</td>
                </tr>
              </tbody>
            )}
          </table>
          <InfinityScrollLoad
            targetId='notification_list'
            isLoading={isLoading}
            callback={notifyEnd => {
              this.setNotifications(notifyEnd);
            }}
          />
          <Modal modalRoot='root' modalState={modalState}>
            {this.renderNotificationDetailModal()}
          </Modal>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/Notifications.jsx